import AppForm, { AppFormValues } from 'apps/common/AppForm';
import PageHeader from 'common/components/PageHeader';
import AuthenticatedLayout from 'common/layouts/AuthenticatedLayout';
import apps from 'common/services/apps';
import {
  AppLimitReachedOrExceededError,
  CreateAppRequest,
  SCOPES,
  WebhookCreationFailedError,
  WebhookConflictError,
} from 'common/types/apps';
import { ApiError } from 'common/services/api';
import React from 'react';
import { useAsyncCallback } from 'react-async-hook';
import { useNavigate } from 'react-router-dom';

function CreateApp() {
  const navigate = useNavigate();

  const toCreateAppRequest = (formValues: AppFormValues): CreateAppRequest => ({
    name: formValues.name,
    contacts: formValues.contacts.map((c) => c.value),
    redirectUrls: formValues.redirectUrls.map((r) => r.value),
    privacyPolicyUrl: formValues.privacyPolicyUrl,
    scopes: Object.entries(formValues.scopes)
      .filter(([, isChecked]) => isChecked)
      .map(([s]) => s),
    logoUrl: formValues.logoUrl,
    webhooks: formValues.webhooks
      .filter((webhook) => webhook.url && webhook.url.trim() !== '')
      .map((webhook) => ({
        url: webhook.url,
        model_version: webhook.model_version,
      })),
  });

  const createAppTask = useAsyncCallback(async (v: AppFormValues) => {
    try {
      const createdApp = await apps.createApp(toCreateAppRequest(v));
      navigate(`/apps/${createdApp.clientId}`);
    } catch (e: any) {
      if (e instanceof AppLimitReachedOrExceededError) {
        throw new Error(
          "You've exceeded your app limit. To request an app increase, please submit a request via https://whoopinc.typeform.com/to/XmzituEp.",
        );
      } else if (e instanceof WebhookCreationFailedError) {
        navigate(`/apps/${e.app.clientId}/edit`, { state: { webhookError: true } });
      } else if (e instanceof WebhookConflictError) {
        navigate(`/apps/${e.app?.clientId}/edit`, { state: { webhookError: true, conflict: true } });
      } else if (e instanceof ApiError) {
        try {
          const bodyText = await e.response.text();
          const parsed = JSON.parse(bodyText);
          const hint = parsed?.error_hint || parsed?.error_description || e.message;
          throw new Error(hint);
        } catch (_) {
          throw new Error(e.message || 'Bad Request');
        }
      } else {
        throw new Error(e);
      }
    }
  });

  return (
    <>
      <PageHeader title="New App" />
      <AppForm
        availableScopes={SCOPES.map((s) => s.scope)}
        defaultValues={{
          contacts: [{ value: '' }],
          redirectUrls: [{ value: '' }],
          name: '',
          privacyPolicyUrl: '',
          scopes: {},
          webhooks: [],
        }}
        cta="Create App"
        loading={createAppTask.loading}
        error={createAppTask.error?.message}
        onSubmit={createAppTask.execute}
      />
    </>
  );
}

export default () => (
  <AuthenticatedLayout pageIdentifier="create-app">
    <CreateApp />
  </AuthenticatedLayout>
);
